import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { useTranslation } from 'react-i18next';

const KeyCap = ({ children }) => (
    <Box
        component="kbd"
        sx={{
            display: 'inline-block',
            minWidth: '28px',
            padding: '3px 8px',
            marginInlineEnd: '4px',
            borderRadius: '5px',
            border: '1px solid #c3c9ff',
            backgroundColor: '#eef1ff',
            color: '#304FFF',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '12px',
            fontWeight: 700,
            textAlign: 'center',
            boxShadow: '0 1px 0 rgba(48, 79, 255, 0.25)',
            whiteSpace: 'nowrap',
        }}
    >
        {children}
    </Box>
);

const ShortcutRow = ({ keys, description }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 12px',
            borderRadius: '8px',
            '&:nth-of-type(odd)': { backgroundColor: '#f7f8ff' },
        }}
    >
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            {keys.map((key, index) => (
                <React.Fragment key={index}>
                    <KeyCap>{key}</KeyCap>
                    {index < keys.length - 1 && (
                        <Typography component="span" sx={{ mx: 0.5, fontSize: '12px', color: '#888' }}>
                            +
                        </Typography>
                    )}
                </React.Fragment>
            ))}
        </Box>
        <Typography sx={{ fontSize: '14px', color: '#333' }}>{description}</Typography>
    </Box>
);

const KeyboardShortcutsDialog = ({ open, onClose, direction }) => {
    const { t } = useTranslation();

    const shortcuts = [
        { keys: ['\u2191', '\u2193'], description: t('KbdGuideNavigateRows') },
        { keys: ['\u2190', '\u2192'], description: t('KbdGuideSwitchPanels') },
        { keys: ['Space'], description: t('KbdGuideToggleSelect') },
        { keys: ['Shift', 'Space'], description: t('KbdGuideRangeSelect') },
        { keys: ['Shift', '\u2191/\u2193'], description: t('KbdGuideRangeExtend') },
        { keys: ['Shift', 'Click'], description: t('KbdGuideShiftClick') },
        { keys: ['F5'], description: t('KbdGuideReconcile') },
        { keys: ['F5'], description: t('KbdGuideReconcileDifference') },
        { keys: ['F6'], description: t('KbdGuidePerRowEntries') },
        { keys: ['Enter'], description: t('KbdGuideDialogNextField') },
        { keys: ['Ctrl', 'Enter'], description: t('KbdGuideConfirmDialog') },
        { keys: ['/'], description: t('KbdGuideSearch') },
        { keys: ['Esc'], description: t('KbdGuideExitSearch') },
        { keys: ['?'], description: t('KbdGuideOpenGuide') },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            dir={direction}
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    backgroundColor: 'white',
                    borderRadius: '10px',
                    padding: '8px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '20px',
                    color: '#304FFF',
                    fontWeight: 700,
                }}
            >
                <KeyboardIcon />
                {t('KbdGuideTitle')}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: '13px', color: '#666', mb: 1.5 }}>
                    {t('KbdGuideIntro')}
                </Typography>
                {shortcuts.map((shortcut, index) => (
                    <ShortcutRow key={index} keys={shortcut.keys} description={shortcut.description} />
                ))}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'center', padding: '12px' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        backgroundColor: '#304FFF',
                        color: '#fff',
                        fontWeight: 'bold',
                        padding: '8px 24px',
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: '#1565c0' },
                    }}
                >
                    {t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default KeyboardShortcutsDialog;
